import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  createPrivacyRequest,
  decidePrivacyRequest,
  deleteLogoRequest,
  fetchBranding,
  fetchPrivacyRequests,
  fetchPublicBranding,
  updateBrandingRequest,
  uploadLogoRequest,
} from '../api/endpoints';
import { applyBranding } from '../branding';
import type { Branding, PrivacyStatus, PrivacyType } from '../types/api';

const keys = {
  branding: ['branding'] as const,
  publicBranding: (slug: string) => ['branding', 'public', slug] as const,
  privacy: (status?: PrivacyStatus) => ['privacy', status ?? 'all'] as const,
};

/** Authenticated branding for the signed-in workspace; applied to the document as it loads. */
export function useTenantBranding(enabled: boolean) {
  const q = useQuery({ queryKey: keys.branding, queryFn: fetchBranding, enabled, staleTime: 5 * 60_000 });
  useEffect(() => {
    if (q.data) applyBranding(q.data);
  }, [q.data]);
  return q;
}

/** Pre-login branding (login, password pages). Unknown workspaces resolve to default branding. */
export function usePublicBranding(slug: string) {
  const valid = /^[a-z0-9-]{2,50}$/.test(slug);
  const q = useQuery({ queryKey: keys.publicBranding(slug), queryFn: () => fetchPublicBranding(slug), enabled: valid, staleTime: 60_000, retry: false });
  useEffect(() => {
    applyBranding(valid && q.data ? q.data : null);
  }, [q.data, valid]);
  return valid ? q.data : undefined;
}

export function useBrandingMutations() {
  const qc = useQueryClient();
  const onSuccess = (b: Branding) => {
    qc.setQueryData<Branding>(keys.branding, (prev) => ({ ...prev, ...b }));
    applyBranding(b);
  };
  return {
    update: useMutation({ mutationFn: (body: Parameters<typeof updateBrandingRequest>[0]) => updateBrandingRequest(body), onSuccess }),
    upload: useMutation({ mutationFn: (file: File) => uploadLogoRequest(file), onSuccess }),
    removeLogo: useMutation({ mutationFn: () => deleteLogoRequest(), onSuccess }),
  };
}

export function usePrivacyRequests(status?: PrivacyStatus) {
  // Poll while anything is still in flight so users see exports complete without reloading.
  return useQuery({
    queryKey: keys.privacy(status),
    queryFn: () => fetchPrivacyRequests(status),
    refetchInterval: (q) => (q.state.data?.some((r) => r.status === 'approved' || r.status === 'processing') ? 5_000 : false),
  });
}

export function usePrivacyMutations() {
  const qc = useQueryClient();
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['privacy'] });
  return {
    create: useMutation({ mutationFn: (b: { type: PrivacyType; subjectId?: string; reason?: string }) => createPrivacyRequest(b), onSuccess: invalidate }),
    decide: useMutation({ mutationFn: (a: { id: string; decision: 'approve' | 'reject'; note?: string }) => decidePrivacyRequest(a.id, a.decision, a.note), onSuccess: invalidate }),
  };
}
